import { useEffect, useState } from 'react';
import { Plus, Truck } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label
} from '@/shared/components/ui';
import { formatApiErrorMessage } from '../Utils';
import type { AddOutOfNetworkSupplierPayload } from '../Types';

interface OutOfNetworkSupplierModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submitting?: boolean;
  onAdd: (payload: AddOutOfNetworkSupplierPayload) => Promise<void>;
}

export const OutOfNetworkSupplierModal = ({
  open,
  onOpenChange,
  submitting,
  onAdd
}: OutOfNetworkSupplierModalProps) => {
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contactName, setContactName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setBusinessName('');
    setEmail('');
    setPhone('');
    setContactName('');
    setError(null);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  const handleClose = () => {
    if (!submitting) {
      onOpenChange(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    const name = businessName.trim();
    if (!name) {
      setError('Business name is required');
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setError('Provide at least an email or phone for order notifications');
      return;
    }

    try {
      await onAdd({
        business_name: name,
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(contactName.trim()
          ? { contact_person_name: contactName.trim() }
          : {})
      });
      onOpenChange(false);
    } catch (err: unknown) {
      setError(formatApiErrorMessage(err, 'Failed to add supplier'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => !submitting && onOpenChange(next)}
    >
      <DialogContent onClose={handleClose} className="max-w-md sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-[#087f8c]" />
            Add out-of-network supplier
          </DialogTitle>
          <DialogDescription>
            For suppliers who do not use Gio — they will receive order
            notifications by email or phone. The supplier will be linked to this
            location.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <div>
              <Label htmlFor="oon-business-name">Business name *</Label>
              <Input
                id="oon-business-name"
                value={businessName}
                onChange={e => setBusinessName(e.target.value)}
                placeholder="e.g. Joe's Produce"
                disabled={submitting}
                autoFocus
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="oon-email">Notification email</Label>
                <Input
                  id="oon-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="orders@supplier.com"
                  disabled={submitting}
                />
              </div>
              <div>
                <Label htmlFor="oon-phone">Notification phone</Label>
                <Input
                  id="oon-phone"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  disabled={submitting}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="oon-contact">Contact person</Label>
              <Input
                id="oon-contact"
                value={contactName}
                onChange={e => setContactName(e.target.value)}
                placeholder="Optional"
                disabled={submitting}
              />
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button
              type="button"
              variant="ghost"
              disabled={submitting}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              loading={submitting}
              className="!bg-[#087f8c] text-white hover:!bg-[#066873]"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add supplier
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
